import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatStepperModule } from '@angular/material/stepper';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatCardModule } from '@angular/material/card';
import { MatTableFilterModule } from 'mat-table-filter';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSidenavModule } from '@angular/material/sidenav';

import { HighlightModule, HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';

import { NgDynamicBreadcrumbModule } from "ng-dynamic-breadcrumb";
import { JSZipObject } from 'jszip';

import { AppComponent } from './app.component';
import { NavMenuComponent } from './nav-menu/nav-menu.component';
import { HomeComponent } from './home/home.component';
import { WellDetailsComponent } from './well-details/well-details.component';
import { LoginComponent } from './login/login.component';

import { AuthGuard } from './_helpers/auth.guard';
import { RegisterComponent } from './register/register.component';
import { JWTInterceptor } from './_helpers/jwtinterceptor';
import { HttpErrorInterceptor } from './_helpers/http-error-interceptor';
import { OtherDataFormComponent } from './other-data-form/other-data-form.component';
import { FluidFractionFormComponent } from './fluid-fraction-form/fluid-fraction-form.component';
import { InjectionDataListComponent } from './injection-data-list/injection-data-list.component';
import { WellDetailsDisplayComponent } from './well-details-display/well-details-display.component';
import { ImportExcelDataComponent } from './import-excel-data/import-excel-data.component';
import * as JSZip from 'jszip';
import { MonthlyDateRangeSelectorComponent } from './monthly-date-range-selector/monthly-date-range-selector.component';
import { DailyDateRangeSelectorComponent } from './daily-date-range-selector/daily-date-range-selector.component';
import { BaseDateRangeSelectorComponent } from './base-date-range-selector/base-date-range-selector.component';
import { ExcelImportService } from './_services/excel-import.service';
import { InjectionRecordEditorComponent } from './injection-record-editor/injection-record-editor.component';
import { ApiResultErrorDialogComponent } from './api-result-error-dialog/api-result-error-dialog.component';
import { ErrorDialogComponent } from './error-dialog/error-dialog.component';
import { MessageDialogComponent } from './message-dialog/message-dialog.component';
import { UserConfirmComponent } from './user-confirm/user-confirm.component';
import { ApproverConfirmComponent } from './approver-confirm/approver-confirm.component';
import { InjectionFormationsEditorComponent } from './injection-formations-editor/injection-formations-editor.component';
import { UserSettingsComponent } from './user-settings/user-settings.component';
import { ExportDateRangeSelectorComponent } from './export-date-range-selector/export-date-range-selector.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { ApproverCPComponent } from './approver-cp/approver-cp.component';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { AuthenticationService } from './_services/authentication.service';
import { BrowseViewComponent } from './browse-view/browse-view.component';
import { AngularSplitModule } from 'angular-split';
import { BrowseWellDetailsComponent } from './browse-well-details/browse-well-details.component';
import { InjectionGraphComponent } from './injection-graph/injection-graph.component';
import { WelcomeDialogComponent } from './welcome-dialog/welcome-dialog.component';
import { ExportInProgressDialogComponent } from './export-in-progress-dialog/export-in-progress-dialog.component';
import { AdminCpComponent } from './admin-cp/admin-cp.component';
import { AdminEditUserComponent } from './admin-edit-user/admin-edit-user.component';
import { ApiDocsComponent } from './api-docs/api-docs.component';
import { SideloadInjectionComponent } from './sideload-injection/sideload-injection.component';
import { EditFormationsDialogComponent } from './edit-formations-dialog/edit-formations-dialog.component';
import { DeleteInjectionDialogComponent } from './delete-injection-dialog/delete-injection-dialog.component';
import { InjectionDensityDataGridComponent } from './injection-density-data-grid/injection-density-data-grid.component';
import { InjectionPressureDataGridComponent } from './injection-pressure-data-grid/injection-pressure-data-grid.component';
import { InjectionCompositionDataGridComponent } from './injection-composition-data-grid/injection-composition-data-grid.component';
import { InjectionIntervalDataGridComponent } from './injection-interval-data-grid/injection-interval-data-grid.component';
import { AddInjection2Component } from './add-injection2/add-injection2.component';
import { AddInjection2MonthlyComponent } from './add-injection2-monthly/add-injection2-monthly.component';
import { InjectionDataGrid2Component } from './injection-data-grid2/injection-data-grid2.component';
import { AddBHPComponent } from './add-bhp/add-bhp.component';
import { InjectionPresubmitDataTableComponent } from './injection-presubmit-data-table/injection-presubmit-data-table.component';
import { UserSubmittableDataGridComponent } from './user-submittable-data-grid/user-submittable-data-grid.component';
import { BhpDataGridComponent } from './bhp-data-grid/bhp-data-grid.component';
import { ValidationFailureReasonsDisplayComponent } from './validation-failure-reasons-display/validation-failure-reasons-display.component';
import { BhpPresubmitDataTableComponent } from './bhp-presubmit-data-table/bhp-presubmit-data-table.component';
import { InjectionPressureRecordEditorComponent } from './injection-pressure-record-editor/injection-pressure-record-editor.component';
import { HelpComponent } from './help/help.component';
import { FaqComponent } from './faq/faq.component';
import { FilterPipe } from './filter.pipe';
import { AddUserComponent } from './add-user/add-user.component';
import { WellLookupComponent } from './well-lookup/well-lookup.component';
import { SelectPeriodDialogComponent } from './select-period-dialog/select-period-dialog.component';
import { BhpPeriodMethodSelectorComponent } from './bhp-period-method-selector/bhp-period-method-selector.component';
import { BhpDialogComponent } from './bhp-dialog/bhp-dialog.component';
import { LoadingDialogComponent } from './loading-dialog/loading-dialog.component';
import { BrowseCardComponent } from './browse-card/browse-card.component';

@NgModule({
  declarations: [
    AppComponent,
    NavMenuComponent,
    HomeComponent,
    WellDetailsComponent,
    LoginComponent,
    RegisterComponent,
    OtherDataFormComponent,
    FluidFractionFormComponent,
    InjectionDataListComponent,
    WellDetailsDisplayComponent,
    ImportExcelDataComponent,
    MonthlyDateRangeSelectorComponent,
    DailyDateRangeSelectorComponent,
    BaseDateRangeSelectorComponent,
    InjectionRecordEditorComponent,
    ApiResultErrorDialogComponent,
    ErrorDialogComponent,
    MessageDialogComponent,
    UserConfirmComponent,
    ApproverConfirmComponent,
    InjectionFormationsEditorComponent,
    UserSettingsComponent,
    ExportDateRangeSelectorComponent,
    ResetPasswordComponent,
    ApproverCPComponent,
    ConfirmDialogComponent,
    BrowseViewComponent,
    BrowseWellDetailsComponent,
    InjectionGraphComponent,
    WelcomeDialogComponent,
    ExportInProgressDialogComponent,
    AdminCpComponent,
    AdminEditUserComponent,
    ExportInProgressDialogComponent,
    ApiDocsComponent,
    SideloadInjectionComponent,
    EditFormationsDialogComponent,
    DeleteInjectionDialogComponent,
    InjectionDensityDataGridComponent,
    InjectionPressureDataGridComponent,
    InjectionCompositionDataGridComponent,
    InjectionIntervalDataGridComponent,
    AddInjection2Component,
    InjectionDataGrid2Component,
    AddBHPComponent,
    InjectionPresubmitDataTableComponent,
    UserSubmittableDataGridComponent,
    BhpDataGridComponent,
    ValidationFailureReasonsDisplayComponent,
    BhpPresubmitDataTableComponent,
    InjectionPressureRecordEditorComponent,
    HelpComponent,
    FaqComponent,
    FilterPipe,
    AddUserComponent,
    WellLookupComponent,
    SelectPeriodDialogComponent,
    BhpPeriodMethodSelectorComponent,
    BhpDialogComponent,
    LoadingDialogComponent,
    AddInjection2MonthlyComponent,
    BrowseCardComponent
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule.forRoot([
      {
        path: '',
        component: BrowseViewComponent,
        pathMatch: 'full',
        data: {
          title: 'Home',
          breadcrumb: [
            {
              label: "Home",
              url: ""
            }
          ]
        }
      },
      {
        path: 'faq',
        component: FaqComponent,
        pathMatch: 'full',
        data: {
          title: 'FAQ',
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "FAQ & Glossary",
              url: ""
            }
          ]
        }
      },
      {
        path: 'help',
        component: HelpComponent,
        pathMatch: 'full',
        data: {
          title: 'Help',
          canActivate: [AuthGuard],
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "Help Videos",
              url: ""
            }
          ]
        }
      },
      {
        path: 'mywells',
        component: HomeComponent,
        pathMatch: 'full',
        data: {
          title: 'My Wells',
          canActivate: [AuthGuard],
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "My Wells",
              url: ""
            }
          ]
        }
      },
      {
        path: 'register',
        component: RegisterComponent,
        pathMatch: 'full',
        data: {
          title: 'Register',
          breadcrumb: [
            {
              label: "Register",
              url: ""
            }
          ]
        }
      },
      {
        path: 'login',
        component: LoginComponent,
        pathMatch: 'full',
        data: {
          title: 'Login',
          breadcrumb: [
            {
              label: "Login",
              url: ""
            }
          ]
        }
      },
      {
        path: 'resetpassword',
        component: ResetPasswordComponent,
        pathMatch: 'full',
        data: {
          title: 'Reset Password',
          breadcrumb: [
            {
              label: "Reset Password",
              url: ""
            }
          ]
        }
      },
      {
        path: 'well/:wellId',
        component: WellDetailsComponent,
        canActivate: [AuthGuard],
        data: {
          title: 'Well Details: {{Uicnumber}}',
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "My Wells",
              url: "/mywells"
            },
            {
              label: "Well Details: {{Uicnumber}}",
              url: ""
            }
          ]
        }
      },
      {
        path: 'well/:wellId/addinjectiondaily',
        component: AddInjection2Component,
        canActivate: [AuthGuard],
        data: {
          dateMode: "daily",
          title: 'Well Details',
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "My Wells",
              url: "/mywells"
            },
            {
              label: "Well Details: {{Uicnumber}}",
              url: "/well/:wellId"
            },
            {
              label: "Add Daily Injection Data",
              url: ""
            }
          ]
        }
      },
      {
        path: 'well/:wellId/addinjectionmonthly',
        component: AddInjection2MonthlyComponent,
        canActivate: [AuthGuard],
        data: {
          dateMode: "daily",
          title: 'Well Details',
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "My Wells",
              url: "/mywells"
            },
            {
              label: "Well Details: {{Uicnumber}}",
              url: "/well/:wellId"
            },
            {
              label: "Add Monthly Injection Data",
              url: ""
            }
          ]
        }
      },
      {
        path: 'well/:wellId/addbhp',
        component: AddBHPComponent,
        canActivate: [AuthGuard],
        data: {
          dateMode: "daily",
          title: 'Well Details',
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "My Wells",
              url: "/mywells"
            },
            {
              label: "Well Details: {{Uicnumber}}",
              url: "/well/:wellId"
            },
            {
              label: "Add BHP Data",
              url: ""
            }
          ]
        }
      },
      {
        path: 'well/:wellId/injection/:injectionId/edit',
        component: InjectionRecordEditorComponent,
        canActivate: [AuthGuard],
        data: {
          dateMode: "daily",
          title: 'Edit',
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "My Wells",
              url: "/mywells"
            },
            {
              label: "Well Details: {{Uicnumber}}",
              url: "/well/:wellId"
            },
            {
              label: "Edit Injection: {{InjectionDate}}",
              url: ""
            }
          ]
        }
      },
      {
        path: 'well/:wellId/injectionpressure/:injectionPressureId/edit',
        component: InjectionPressureRecordEditorComponent,
        canActivate: [AuthGuard],
        data: {
          dateMode: "daily",
          title: 'Edit',
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "My Wells",
              url: "/mywells"
            },
            {
              label: "Well Details: {{Uicnumber}}",
              url: "/well/:wellId"
            },
            {
              label: "Edit Injection Pressure: {{MeasurementDate}}",
              url: ""
            }
          ]
        }
      },
      {
        path: 'usersettings',
        component: UserSettingsComponent,
        canActivate: [AuthGuard],
        data: {
          title: "User Settings",
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "User Settings",
              url: ""
            },
          ]
        }
      },
      {
        path: 'userconfirm/:userid/:confirmationcode',
        component: UserConfirmComponent,
        data: {
          breadcrumb: [
            {
              label: "User Confirmation",
              url: ""
            }
          ]
        }
      },
      {
        path: 'approverconfirm/:userid/:operatorid/:confirmationcode/:approvedeny',
        component: ApproverConfirmComponent,
        canActivate: [AuthGuard],
        data: {
          breadcrumb: [
            {
              label: "User Approval",
              url: ""
            }
          ]
        }
      },
      {
        path: 'approvercp',
        component: ApproverCPComponent,
        canActivate: [AuthGuard],
        data: {
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "Approver Control Panel",
              url: ""
            }
          ]
        }
      },
      {
        path: 'admincp',
        component: AdminCpComponent,
        canActivate: [AuthGuard],
        data: {
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "Admin Control Panel",
              url: ""
            }
          ]
        }
      },
      {
        path: 'admincp/user/new',
        component: AddUserComponent,
        canActivate: [AuthGuard],
        data: {
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "Admin Control Panel",
              url: "/admincp"
            },
            {
              label: "Add New User",
              url: ""
            }
          ]
        }
      },
      {
        path: 'lookupwell',
        component: WellLookupComponent,
        canActivate: [AuthGuard],
        data: {
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "Well Lookup",
              url: ""
            }
          ]
        }
      },
      {
        path: 'sideload',
        component: SideloadInjectionComponent,
        canActivate: [AuthGuard],
        data: {
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "Side Load Injection Data",
              url: ""
            }
          ]
        }
      },
      {
        path: 'admincp/user/:userid',
        component: AdminEditUserComponent,
        canActivate: [AuthGuard],
        data: {
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "Admin Control Panel",
              url: "/admincp"
            },
            {
              label: "User Details",
              url: ""
            }
          ]
        }
      },
      {
        path: "apidocs",
        component: ApiDocsComponent,
        data: {
          breadcrumb: [
            {
              label: "Home",
              url: "/"
            },
            {
              label: "API Documentation",
              url: ""
            }
          ]
        }

      }
    ]),
    MatToolbarModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatSortModule,
    MatPaginatorModule,
    MatStepperModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatRadioModule,
    MatCardModule,
    MatTableFilterModule,
    MatMenuModule,
    MatDialogModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    BrowserAnimationsModule,
    NgDynamicBreadcrumbModule,
    MatListModule,
    MatTooltipModule,
    MatTabsModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatSidenavModule,
    AngularSplitModule,
    HighlightModule
  ],
  entryComponents: [
    ImportExcelDataComponent,
    InjectionRecordEditorComponent,
    ApiResultErrorDialogComponent,
    ExportDateRangeSelectorComponent,
    MessageDialogComponent,
    BrowseWellDetailsComponent
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: JWTInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        lineNumbersLoader: () => import('highlightjs-line-numbers.js'), // Optional, only if you want the line numbers
        languages: {
          python: () => import('highlight.js/lib/languages/python')
        }
      }
    },
    MatDatepickerModule,
    MatMomentDateModule,
    ExcelImportService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
